import { RadiusConfig, ZoomConfig } from '@/config/types'

export type BaseCoordinates = {
  lat: number
  lng: number
}

export type BaseAddress = {
  streetNumber?: string
  streetName?: string
  city?: string
  region?: string
  postalCode?: string
  country?: string
}

export interface BasePlace extends Partial<BaseCoordinates> {
  id: string
  htmlStr?: string
  formattedAddress?: string
  distance?: string
  address?: BaseAddress
  type?: PlaceType
}

export enum PlaceType {
  Country = 5,
  AdministrativeArea = 7,
  Locality = 12,
  Neighborhood = 14,
  Route = 15,
  Intersection = 17,
  Premise = 18,
  PointOfInterest = 19,
  Unrecognized = 13
}

// Keep the original function for backward compatibility but mark as deprecated
/**
 * @deprecated Use getLocaleAwarePlaceTypeRadius instead for locale-specific radius
 */
export function getPlaceTypeRadius(placeType?: PlaceType): number | null {
  const radiusMap: Record<PlaceType, number | null> = {
    [PlaceType.Country]: 50000,
    [PlaceType.AdministrativeArea]: 25000,
    [PlaceType.Locality]: 8000,
    [PlaceType.Neighborhood]: 2000,
    [PlaceType.Route]: 1000,
    [PlaceType.Intersection]: 200,
    [PlaceType.Premise]: 100,
    [PlaceType.PointOfInterest]: 500,
    [PlaceType.Unrecognized]: null
  }

  if (placeType) {
    return radiusMap[placeType]
  } else {
    return null
  }
}

export function getLocaleAwarePlaceTypeRadius(
  placeType?: PlaceType,
  radiusConfig?: RadiusConfig
): number | null {
  if (!placeType || !radiusConfig) {
    return getPlaceTypeRadius(placeType) // Fallback to original function
  }

  const radiusMap: Record<PlaceType, number | null> = {
    [PlaceType.Country]: radiusConfig.country,
    [PlaceType.AdministrativeArea]: radiusConfig.administrativeArea,
    [PlaceType.Locality]: radiusConfig.locality,
    [PlaceType.Neighborhood]: radiusConfig.neighborhood,
    [PlaceType.Route]: radiusConfig.route,
    [PlaceType.Intersection]: radiusConfig.intersection,
    [PlaceType.Premise]: radiusConfig.premise,
    [PlaceType.PointOfInterest]: radiusConfig.pointOfInterest,
    [PlaceType.Unrecognized]: null
  }

  return radiusMap[placeType]
}

export function getLocaleAwarePlaceTypeZoom(
  placeType?: PlaceType,
  zoomConfig?: ZoomConfig
): number {
  if (!placeType || !zoomConfig) {
    return placeType || zoomConfig?.default || PlaceType.Country
  }

  const zoomMap: Record<PlaceType, number> = {
    [PlaceType.Country]: zoomConfig.country,
    [PlaceType.AdministrativeArea]: zoomConfig.large,
    [PlaceType.Locality]: zoomConfig.medium,
    [PlaceType.Neighborhood]: zoomConfig.small,
    [PlaceType.Route]: zoomConfig.verySmall,
    [PlaceType.Intersection]: zoomConfig.verySmall,
    [PlaceType.Premise]: zoomConfig.verySmall,
    [PlaceType.PointOfInterest]: zoomConfig.small,
    [PlaceType.Unrecognized]: zoomConfig.default
  }

  return zoomMap[placeType]
}
